"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Alert } from "@/components/ui/Alert";
import { useDict } from "@/components/i18n/DictProvider";
import { refreshModuleAccess } from "@/lib/lecturehub/module-actions";

/**
 * Shown after a successful Stripe checkout redirect (`?paid=1`). Polls the
 * server every 2 seconds (revalidates the module cache + router.refresh)
 * until `canView` flips to true (granted by the Stripe webhook). After 30
 * seconds the banner switches to a "this is taking longer than usual"
 * variant so the user knows to contact support if something stuck.
 *
 * Renders nothing when there's no `?paid=1`, or once `canView` is true.
 */
export function PaidProcessingBanner({
  moduleId,
  canView,
}: {
  moduleId: number;
  canView: boolean;
}) {
  const t = useDict();
  const router = useRouter();
  const sp = useSearchParams();
  const paid = sp.get("paid") === "1";
  const [elapsed, setElapsed] = useState(0);
  const startedAt = useRef<number | null>(null);

  useEffect(() => {
    if (!paid || canView) return;
    if (startedAt.current == null) startedAt.current = Date.now();

    const tick = async () => {
      setElapsed(Math.floor((Date.now() - (startedAt.current ?? Date.now())) / 1000));
      await refreshModuleAccess(moduleId);
      router.refresh();
    };

    const interval = setInterval(tick, 2000);
    tick(); // immediate first poll
    return () => clearInterval(interval);
  }, [paid, canView, moduleId, router]);

  if (!paid || canView) return null;

  const timedOut = elapsed >= 30;

  return (
    <div data-section="paid-processing-banner">
      <Alert tone={timedOut ? "warning" : "info"} className="mb-4">
        {timedOut ? t.checkout.paidTimeout : t.checkout.paidProcessing}
      </Alert>
    </div>
  );
}
